import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Plus, Trash2, ExternalLink, Save, Send, MessageCircle } from 'lucide-react';

const DEFAULT_SECTIONS = [
  'Morning Drink', 'Breakfast', 'Mid-Morning', 'Lunch', 'Evening Snack', 'Dinner', 'Bedtime',
];

const StatusPill = ({ status }) => {
  const map = {
    consultation_booked: ['Consultation Booked', 'bg-slate-100 text-slate-700 border-slate-200'],
    preparing: ['Preparing', 'bg-amber-50 text-amber-800 border-amber-200'],
    ready: ['Ready', 'bg-emerald-50 text-emerald-800 border-emerald-200'],
    completed: ['Completed', 'bg-sans_bone text-sans_ink border-sans_line'],
  };
  const [label, cls] = map[status] || map.consultation_booked;
  return <span className={`text-[10px] uppercase tracking-[0.22em] border px-2.5 py-1 ${cls}`}>{label}</span>;
};

const InputCls = 'w-full border border-sans_line bg-white px-3 py-2 text-sm text-sans_ink focus:outline-none focus:border-sans_ink';

const DietPlanEditor = ({ appointmentId, onDelivered }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [delivering, setDelivering] = useState(false);
  const [suggestions, setSuggestions] = useState({});
  const [data, setData] = useState(null);
  const [plan, setPlan] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [{ data: d }, { data: s }] = await Promise.all([
          api.get(`/diet/plans/${appointmentId}`),
          api.get('/diet/section-suggestions'),
        ]);
        if (!alive) return;
        setData(d);
        // Ensure all default sections exist
        const existing = d.plan.sections || [];
        const bySlug = Object.fromEntries(existing.map((s) => [s.meal, s]));
        const merged = DEFAULT_SECTIONS.map((meal) => bySlug[meal] || { meal, dishes: [] });
        setPlan({ ...d.plan, sections: merged });
        setSuggestions(s);
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Failed to load diet plan');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [appointmentId]);

  if (loading) return <div className="text-sans_mute">Loading plan…</div>;
  if (!plan) return <div className="text-sans_mute">Plan unavailable.</div>;

  const patientPhone = (data?.patient?.phone || '').replace(/\D/g, '');
  const waNumber = patientPhone ? (patientPhone.length === 10 ? `91${patientPhone}` : patientPhone) : '';
  const waMsg = encodeURIComponent(
    `Hi ${data?.patient?.name || ''}, your personalised Saans Ayurved diet plan is ready. You can view it in your dashboard at https://saansayurved.com/dashboard`
  );
  const waLink = waNumber ? `https://wa.me/${waNumber}?text=${waMsg}` : null;

  const updSection = (idx, patch) => {
    const sections = [...plan.sections];
    sections[idx] = { ...sections[idx], ...patch };
    setPlan({ ...plan, sections });
  };

  const addDish = (sIdx) => {
    const sections = [...plan.sections];
    sections[sIdx].dishes = [...(sections[sIdx].dishes || []), { name: '', notes: '', recipe_url: '' }];
    setPlan({ ...plan, sections });
  };

  const updDish = (sIdx, dIdx, patch) => {
    const sections = [...plan.sections];
    const dishes = [...sections[sIdx].dishes];
    dishes[dIdx] = { ...dishes[dIdx], ...patch };
    sections[sIdx] = { ...sections[sIdx], dishes };
    setPlan({ ...plan, sections });
  };

  const rmDish = (sIdx, dIdx) => {
    const sections = [...plan.sections];
    sections[sIdx].dishes = sections[sIdx].dishes.filter((_, i) => i !== dIdx);
    setPlan({ ...plan, sections });
  };

  const addSuggestion = (sIdx, name) => {
    const existing = plan.sections[sIdx].dishes || [];
    if (existing.some((d) => d.name === name)) return;
    updSection(sIdx, { dishes: [...existing, { name, notes: '', recipe_url: '' }] });
  };

  const save = async (nextStatus) => {
    setSaving(true);
    try {
      const payload = {
        sections: plan.sections,
        general_guidelines: plan.general_guidelines || '',
        foods_to_favour: plan.foods_to_favour || '',
        foods_to_avoid: plan.foods_to_avoid || '',
        status: nextStatus || plan.status || 'preparing',
      };
      const { data: updated } = await api.put(`/diet/plans/${appointmentId}`, payload);
      setPlan((p) => ({ ...updated, sections: p.sections }));
      toast.success('Plan saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const deliver = async () => {
    setDelivering(true);
    try {
      await save('ready');
      await api.post(`/diet/plans/${appointmentId}/deliver`);
      setPlan((p) => ({ ...p, status: 'ready' }));
      toast.success('Plan delivered — patient notified');
      onDelivered && onDelivered();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delivery failed');
    } finally {
      setDelivering(false);
    }
  };

  const { patient, appointment, assessment } = data;

  return (
    <div>
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4 border-b border-sans_line pb-6">
        <div>
          <div className="sans-label mb-2">Diet Consultation · ₹2,900</div>
          <div className="font-serif text-2xl text-sans_ink">{patient?.name || appointment.user_name}</div>
          <div className="text-xs text-sans_mute mt-1">{patient?.email} {patient?.phone ? `· ${patient.phone}` : ''}</div>
          <div className="text-xs text-sans_mute mt-1">
            Consulted {new Date(appointment.slot_datetime).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <StatusPill status={plan.status} />
          {plan.updated_at && <div className="text-[11px] text-sans_mute">Last updated {new Date(plan.updated_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}</div>}
        </div>
      </div>

      {/* Patient context */}
      {assessment?.report && (
        <div className="mt-6 grid md:grid-cols-3 gap-4 text-sm">
          <div className="border border-sans_line bg-sans_bone p-4">
            <div className="text-[10px] uppercase tracking-[0.22em] text-sans_mute">Prakriti</div>
            <div className="font-serif text-lg text-sans_ink mt-1">{assessment.report.primary_dosha} / {assessment.report.secondary_dosha}</div>
          </div>
          <div className="border border-sans_line bg-sans_bone p-4">
            <div className="text-[10px] uppercase tracking-[0.22em] text-sans_mute">Agni (digestion)</div>
            <div className="text-sans_ink mt-1 text-sm line-clamp-3">{assessment.report.agni_analysis || '—'}</div>
          </div>
          <div className="border border-sans_line bg-sans_bone p-4">
            <div className="text-[10px] uppercase tracking-[0.22em] text-sans_mute">Concern</div>
            <div className="text-sans_ink mt-1">{assessment.concern || '—'}</div>
          </div>
        </div>
      )}
      {appointment.reason && (
        <div className="mt-4 border border-sans_line bg-white p-4 text-sm">
          <div className="sans-label mb-1">Patient reason</div>
          <p className="text-sans_ink">{appointment.reason}</p>
        </div>
      )}

      {/* Editor sections */}
      <div className="mt-10 space-y-8">
        {plan.sections.map((sec, sIdx) => (
          <section key={sec.meal} className="border border-sans_line bg-white">
            <div className="flex items-center justify-between px-5 py-3 border-b border-sans_line bg-sans_bone/40">
              <div className="font-serif text-xl text-sans_ink">{sec.meal}</div>
              <button
                type="button"
                onClick={() => addDish(sIdx)}
                data-testid={`add-dish-${sec.meal.replace(/\s+/g, '-').toLowerCase()}`}
                className="text-xs text-sans_ink border border-sans_line px-3 py-1.5 hover:bg-white inline-flex items-center gap-1.5"
              >
                <Plus className="w-3.5 h-3.5" strokeWidth={1.75} /> Add dish
              </button>
            </div>

            {/* Suggestion pool */}
            {(suggestions[sec.meal] || []).length > 0 && (
              <div className="px-5 py-3 border-b border-sans_line text-xs">
                <span className="text-sans_mute mr-2">Quick add:</span>
                {suggestions[sec.meal].map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => addSuggestion(sIdx, s)}
                    className="inline-block mr-2 mb-1 text-sans_ink border border-sans_line bg-sans_bone/40 px-2.5 py-1 hover:bg-sans_ink hover:text-sans_bone transition-colors"
                  >
                    + {s}
                  </button>
                ))}
              </div>
            )}

            {sec.dishes.length === 0 && (
              <div className="px-5 py-8 text-sm text-sans_mute italic">
                No dishes yet — add one manually or click a suggestion above. Patient will mix and match from this pool.
              </div>
            )}

            {sec.dishes.map((d, dIdx) => (
              <div key={dIdx} className="grid grid-cols-12 gap-2 px-5 py-3 border-b border-sans_line last:border-0 items-start">
                <input
                  className={`${InputCls} col-span-4`}
                  placeholder="Dish name"
                  value={d.name}
                  onChange={(e) => updDish(sIdx, dIdx, { name: e.target.value })}
                />
                <input
                  className={`${InputCls} col-span-4`}
                  placeholder="Notes (portion, prep tip, when to eat)"
                  value={d.notes}
                  onChange={(e) => updDish(sIdx, dIdx, { notes: e.target.value })}
                />
                <input
                  className={`${InputCls} col-span-3`}
                  placeholder="Recipe URL (YouTube etc)"
                  value={d.recipe_url}
                  onChange={(e) => updDish(sIdx, dIdx, { recipe_url: e.target.value })}
                />
                <button
                  onClick={() => rmDish(sIdx, dIdx)}
                  className="col-span-1 text-sans_mute hover:text-red-600 justify-self-end mt-2"
                  aria-label="Remove"
                >
                  <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                </button>
              </div>
            ))}
          </section>
        ))}
      </div>

      {/* Guidelines + do/avoid */}
      <div className="mt-10 grid md:grid-cols-2 gap-6">
        <div className="md:col-span-2">
          <label className="sans-label block mb-2">General guidelines</label>
          <textarea
            rows={3}
            className={InputCls}
            placeholder="E.g. Eat main meals at fixed times; avoid ice-cold drinks; sip warm cumin water; walk 15 min after lunch…"
            value={plan.general_guidelines || ''}
            onChange={(e) => setPlan({ ...plan, general_guidelines: e.target.value })}
          />
        </div>
        <div>
          <label className="sans-label block mb-2">Foods to favour</label>
          <textarea
            rows={4}
            className={InputCls}
            placeholder="Warm, cooked, freshly prepared meals · ghee · seasonal fruit · soaked almonds…"
            value={plan.foods_to_favour || ''}
            onChange={(e) => setPlan({ ...plan, foods_to_favour: e.target.value })}
          />
        </div>
        <div>
          <label className="sans-label block mb-2">Foods to avoid</label>
          <textarea
            rows={4}
            className={InputCls}
            placeholder="Deep-fried food · leftover meals · ice-cold drinks · excess caffeine…"
            value={plan.foods_to_avoid || ''}
            onChange={(e) => setPlan({ ...plan, foods_to_avoid: e.target.value })}
          />
        </div>
      </div>

      {/* Actions */}
      <div className="mt-10 flex flex-wrap items-center gap-3 border-t border-sans_line pt-6">
        <button
          onClick={() => save()}
          disabled={saving}
          data-testid="diet-save-btn"
          className="sans-btn-secondary inline-flex items-center gap-2"
        >
          <Save className="w-4 h-4" strokeWidth={1.5} /> {saving ? 'Saving…' : 'Save draft'}
        </button>
        {plan.status !== 'ready' && plan.status !== 'completed' && (
          <button
            onClick={deliver}
            disabled={delivering || saving}
            data-testid="diet-deliver-btn"
            className="sans-btn-primary inline-flex items-center gap-2"
          >
            <Send className="w-4 h-4" strokeWidth={1.5} /> {delivering ? 'Delivering…' : 'Deliver to patient'}
          </button>
        )}
        {plan.status === 'ready' && (
          <button
            onClick={() => save('completed')}
            disabled={saving}
            className="sans-btn-secondary inline-flex items-center gap-2"
          >
            Mark as completed
          </button>
        )}
        {waLink && plan.status === 'ready' && (
          <a
            href={waLink}
            target="_blank"
            rel="noreferrer"
            data-testid="diet-whatsapp-btn"
            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] border border-emerald-600 text-emerald-700 px-4 py-2.5 hover:bg-emerald-600 hover:text-white transition-colors"
          >
            <MessageCircle className="w-4 h-4" strokeWidth={1.75} /> Share on WhatsApp
          </a>
        )}
        <div className="text-xs text-sans_mute ml-auto italic">
          Delivering the plan will email the patient and unlock “My Diet Chart” on their dashboard.
        </div>
      </div>
    </div>
  );
};

export default DietPlanEditor;
